import {
  GetObjectCommand,
  GetObjectCommandOutput,
  ListObjectsV2Command,
  ListObjectsV2CommandInput,
  ListObjectsV2CommandOutput,
  S3Client,
  S3ClientConfig,
  S3ServiceException,
} from '@aws-sdk/client-s3';
import { AwsRegion, DEFAULT_AWS_REGION } from 'src/types/common/api.types';
import { getAWSServiceCredentials } from './authUtils';
import { dateUtils } from './dateUtils';
import { logger } from './logger';

/**
 * Error messages for S3 operations
 */
export const S3ErrorMessages = {
  NO_DATA_FOUND: 'No data found in S3',
  INVALID_S3_URI: 'Invalid S3 URI',
  REQUEST_ABORTED: 'Request was aborted',
  DOWNLOAD_FAILED: 'Failed to download content file from S3',
  LIST_OBJECTS_FAILED: 'Failed to list objects',
  UNEXPECTED_ERROR: 'An unexpected error occurred',
} as const;

/**
 * S3 URI parsed result interface
 */
interface ParsedS3URI {
  bucket: string;
  s3Key: string;
}

/**
 * Calculate time difference in milliseconds between two dates
 * @param startTime - Start time as Date
 * @returns Time difference in milliseconds
 */
const getTimeDifferenceMs = (startTime: Date): number => {
  return dateUtils.now().getTime() - startTime.getTime();
};

/**
 * Format bytes to human readable format
 * @param bytes - Number of bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Check if an error is an abort error
 * @param error - Error to check
 * @returns True if the error is an abort error
 */
const isAbortError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return error.name === 'AbortError' || error.message === S3ErrorMessages.REQUEST_ABORTED;
  }
  return false;
};

/**
 * Create a standardized abort error
 * @returns AbortError instance
 */
const createAbortError = (): Error => {
  const abortError = new Error(S3ErrorMessages.REQUEST_ABORTED);
  abortError.name = 'AbortError';
  return abortError;
};

/**
 * Cache duration for S3 client (15 minutes)
 * Credentials from Cognito typically last 1 hour, so 15 minutes is safe
 */
const S3_CLIENT_CACHE_DURATION_MS = 15 * 60 * 1000;

/**
 * S3 client cache entry containing the client, creation timestamp, and pending promise
 */
interface S3ClientCacheEntry {
  client: S3Client;
  timestamp: number;
}

/**
 * S3 client cache using a Map keyed by region
 * This pattern provides:
 * - Multi-region support without overwriting other regions' clients
 * - Proper resource cleanup via destroy() when clients are evicted
 * - Race condition prevention via pending promise tracking
 */
const s3ClientCache = new Map<AwsRegion, S3ClientCacheEntry>();

/**
 * Pending client creation promises to prevent race conditions
 * Multiple concurrent calls for the same region will share the same promise
 */
const pendingClientCreation = new Map<AwsRegion, Promise<S3Client>>();

/**
 * Clear the S3 client cache for all regions
 * Properly destroys all cached S3Client instances to prevent memory leaks
 *
 * @remarks
 * **IMPORTANT FOR TESTING:** This function MUST be called in test teardown (afterEach/afterAll)
 * to prevent module state from persisting between tests. Example:
 * ```typescript
 * afterEach(() => {
 *   clearS3ClientCache();
 * });
 * ```
 *
 * @param region - Optional region to clear. If not provided, clears all regions.
 */
export const clearS3ClientCache = (region?: AwsRegion): void => {
  if (region) {
    // Clear specific region
    const entry = s3ClientCache.get(region);
    if (entry) {
      entry.client.destroy();
      s3ClientCache.delete(region);
    }
    pendingClientCreation.delete(region);
  } else {
    // Clear all regions - destroy all cached clients to prevent memory leaks
    s3ClientCache.forEach((entry) => {
      entry.client.destroy();
    });
    s3ClientCache.clear();
    pendingClientCreation.clear();
  }
};

/**
 * Check if a cached client is still valid (not expired)
 * @param entry - Cache entry to check
 * @param now - Current timestamp
 * @returns True if the cache entry is still valid
 */
const isCacheEntryValid = (entry: S3ClientCacheEntry | undefined, now: number): entry is S3ClientCacheEntry => {
  return entry !== undefined && now - entry.timestamp < S3_CLIENT_CACHE_DURATION_MS;
};

/**
 * Create a new S3 client and cache it
 * @param region - AWS region for the S3 client
 * @returns Promise resolving to the new S3Client
 */
const createAndCacheClient = async (region: AwsRegion): Promise<S3Client> => {
  // Clean up old client if exists (prevents memory leak)
  const oldEntry = s3ClientCache.get(region);
  if (oldEntry) {
    oldEntry.client.destroy();
  }

  // Create new S3 client
  const s3ClientConfig: S3ClientConfig = {
    region,
    credentials: await getAWSServiceCredentials(),
  };
  const client = new S3Client(s3ClientConfig);

  // Cache the client
  s3ClientCache.set(region, {
    client,
    timestamp: Date.now(),
  });

  return client;
};

/**
 * Creates and returns an authenticated S3 client
 * Uses caching to avoid repeated Cognito calls for credentials
 *
 * This implementation:
 * - Caches clients per-region to support multi-region scenarios
 * - Prevents race conditions by sharing pending creation promises
 * - Properly cleans up old clients to prevent memory leaks
 *
 * @param region - AWS region for the S3 client (default: DEFAULT_AWS_REGION)
 * @returns Promise resolving to configured S3Client instance
 */
export const getS3Client = async (region: AwsRegion = DEFAULT_AWS_REGION): Promise<S3Client> => {
  const now = Date.now();

  // Check if we have a valid cached client for this region
  const cachedEntry = s3ClientCache.get(region);
  if (isCacheEntryValid(cachedEntry, now)) {
    return cachedEntry.client;
  }

  // Check if there's already a pending creation for this region
  // This prevents race conditions where multiple concurrent calls create multiple clients
  const pendingCreation = pendingClientCreation.get(region);
  if (pendingCreation) {
    return pendingCreation;
  }

  // Create a new client and track the pending promise
  const creationPromise = createAndCacheClient(region).finally(() => {
    // Clean up the pending promise after creation completes (success or failure)
    pendingClientCreation.delete(region);
  });

  pendingClientCreation.set(region, creationPromise);

  return creationPromise;
};

/**
 * Lists objects from S3 bucket with the specified prefix
 * @param bucket - S3 bucket name
 * @param s3Key - S3 key prefix to filter objects
 * @param signal - Optional AbortSignal for cancelling the request
 * @returns Promise resolving to ListObjectsV2CommandOutput
 */
export const getListOfObjectsFromKeys = async (
  bucket: string,
  s3Key: string,
  signal?: AbortSignal
): Promise<ListObjectsV2CommandOutput> => {
  if (signal?.aborted) {
    throw createAbortError();
  }

  const s3Client = await getS3Client();
  const listCommandInput: ListObjectsV2CommandInput = {
    Bucket: bucket,
    Prefix: s3Key,
  };
  const listCommand = new ListObjectsV2Command(listCommandInput);

  try {
    return await s3Client.send(listCommand, { abortSignal: signal });
  } catch (error) {
    if (signal?.aborted || isAbortError(error)) {
      throw createAbortError();
    }
    throw error;
  }
};

/**
 * Retrieves all object keys from S3 bucket with pagination support
 * @param bucket - S3 bucket name
 * @param s3Key - S3 key prefix to filter objects
 * @param signal - Optional AbortSignal for cancelling the request
 * @returns Promise resolving to array of all object keys
 */
export const getAllObjectKeysFromS3 = async (
  bucket: string,
  s3Key: string,
  signal?: AbortSignal
): Promise<string[]> => {
  if (signal?.aborted) {
    throw createAbortError();
  }

  const s3Client = await getS3Client();
  const allKeys: string[] = [];
  let continuationToken: string | undefined;

  try {
    do {
      // Check for abort before each iteration
      if (signal?.aborted) {
        throw createAbortError();
      }

      const listCommandInput: ListObjectsV2CommandInput = {
        Bucket: bucket,
        Prefix: s3Key,
        ContinuationToken: continuationToken,
      };

      const listCommand = new ListObjectsV2Command(listCommandInput);
      let listOfObjects: ListObjectsV2CommandOutput;

      try {
        listOfObjects = await s3Client.send(listCommand, { abortSignal: signal });
      } catch (error) {
        if (signal?.aborted || isAbortError(error)) {
          throw createAbortError();
        }
        if (error instanceof S3ServiceException) {
          logger.error(`S3 service error: ${error.message}`);
          throw new Error(
            `${S3ErrorMessages.LIST_OBJECTS_FAILED}: bucket: ${bucket}, key: ${s3Key}, error: ${error.message}`
          );
        }
        logger.error(`Unexpected error: ${error}`);
        throw new Error(`${S3ErrorMessages.UNEXPECTED_ERROR} while listing objects: bucket: ${bucket}, key: ${s3Key}`);
      }

      if (listOfObjects.Contents) {
        const keys = listOfObjects.Contents.map((obj) => obj.Key).filter((key): key is string => key !== undefined);
        allKeys.push(...keys);
      }

      continuationToken = listOfObjects.NextContinuationToken;
    } while (continuationToken);

    logger.info(`Successfully retrieved ${allKeys.length} keys from bucket ${bucket} with prefix ${s3Key}`);
    return allKeys;
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    logger.error(`Error in getAllObjectKeysFromS3: ${error}`);
    throw error;
  }
};

/**
 * Parses an S3 URI to extract the bucket name and key
 * @param s3URI - The S3 URI to parse (e.g., s3://bucket-name/key)
 * @returns An object containing the bucket name and key
 * @throws Error if the S3 URI is invalid
 */
export const parseS3URI = (s3URI: string): ParsedS3URI => {
  const match = s3URI.match(/^s3:\/\/([^/]+)\/(.+)/);
  if (!match) {
    throw new Error(`${S3ErrorMessages.INVALID_S3_URI}: ${s3URI}`);
  }

  const [, bucket, s3Key] = match;
  return { bucket, s3Key };
};

/**
 * Options for downloading a file from S3
 */
export interface GetFileFromS3Options {
  /** Optional AbortSignal for cancelling the request */
  signal?: AbortSignal;
  /** AWS region for the S3 client (default: DEFAULT_AWS_REGION) */
  region?: AwsRegion;
  /**
   * Cache control header for the response
   * - 'no-cache': Always fetch fresh data (default)
   * - 'max-age=3600': Cache for 1 hour
   * - 'max-age=86400': Cache for 24 hours
   */
  cacheControl?: string;
}

/**
 * Result of a successful GetObject download: the parsed bucket/key, the raw
 * response body, the elapsed download time, and some metadata.
 */
interface S3ObjectDownload {
  bucket: string;
  s3Key: string;
  body: NonNullable<GetObjectCommandOutput['Body']>;
  contentLength: number;
  contentType?: string;
  durationMs: number;
}

/**
 * Downloads an S3 object via GetObject and returns its raw body for the caller to
 * transform.
 *
 * @param s3URI - The S3 URI of the object to download (e.g., s3://bucket-name/key)
 * @param options - Optional settings for the request (signal, region, cacheControl)
 * @returns Promise resolving to S3ObjectDownload.
 * @throws Error if there is an issue downloading the object or request is aborted
 */
const downloadS3Object = async (s3URI: string, options: GetFileFromS3Options): Promise<S3ObjectDownload> => {
  const { signal, region = DEFAULT_AWS_REGION, cacheControl = 'no-cache' } = options;
  const startTime = dateUtils.now();

  // Check if aborted before starting
  if (signal?.aborted) {
    throw createAbortError();
  }

  try {
    const s3Client = await getS3Client(region);
    const { bucket, s3Key } = parseS3URI(s3URI);

    // Check if aborted after getting client
    if (signal?.aborted) {
      throw createAbortError();
    }

    // Get the file directly (no HeadObject call to reduce API calls)
    const getCommand = new GetObjectCommand({
      Bucket: bucket,
      Key: s3Key,
      ResponseCacheControl: cacheControl,
    });

    let getResponse;
    try {
      getResponse = await s3Client.send(getCommand, { abortSignal: signal });
    } catch (getError: unknown) {
      if (signal?.aborted || isAbortError(getError)) {
        throw createAbortError();
      }

      // Handle NoSuchKey error (file not found)
      const error = getError as { name?: string; Code?: string };
      if (error.name === 'NoSuchKey' || error.Code === 'NoSuchKey') {
        throw new Error(`${S3ErrorMessages.NO_DATA_FOUND}: bucket: ${bucket}, key: ${s3Key}`);
      }
      throw getError;
    }

    // Check if aborted before reading the body
    if (signal?.aborted) {
      throw createAbortError();
    }

    const body = await getResponse.Body;
    if (!body) {
      throw new Error(`${S3ErrorMessages.DOWNLOAD_FAILED}: bucket: ${bucket}, key: ${s3Key}`);
    }

    const durationMs = getTimeDifferenceMs(startTime);
    return {
      bucket,
      s3Key,
      body,
      contentLength: getResponse.ContentLength || 0,
      contentType: getResponse.ContentType,
      durationMs,
    };
  } catch (error: unknown) {
    // Convert abort errors to a standard format
    if (signal?.aborted || isAbortError(error)) {
      throw createAbortError();
    }

    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`Error while downloading file from S3: ${errorMessage}`);
    throw error;
  }
};

/**
 * Downloads a file from S3 using its URI
 * @param s3URI - The S3 URI of the file to download (e.g., s3://bucket-name/key)
 * @param options - Optional settings for the request (signal, region, cacheControl)
 * @returns Promise resolving to the parsed JSON data from the file
 * @throws Error if there is an issue downloading the file or request is aborted
 */
export const getFileFromS3URI = async <T = unknown>(s3URI: string, options: GetFileFromS3Options = {}): Promise<T> => {
  const { signal } = options;
  const { bucket, s3Key, body, durationMs, contentLength } = await downloadS3Object(s3URI, options);

  try {
    const text = await body.transformToString('utf-8');
    if (!text) {
      throw new Error(`${S3ErrorMessages.DOWNLOAD_FAILED}: bucket: ${bucket}, key: ${s3Key}`);
    }

    logger.debug(
      `File downloaded from S3. Bucket: ${bucket}, S3 Key: ${s3Key}, ` +
        `File Size in S3: ${formatBytes(contentLength)}, ` +
        `File Size After Fetch: ${formatBytes(text.length)}, Duration: ${durationMs}ms.`
    );

    return JSON.parse(text) as T;
  } catch (error: unknown) {
    if (signal?.aborted || isAbortError(error)) {
      throw createAbortError();
    }
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`Error while downloading file from S3: ${errorMessage}`);
    throw error;
  }
};

/**
 * Downloads an S3 object as a raw Blob.
 * @param s3URI - The S3 URI of the object to download (e.g., s3://bucket-name/key)
 * @param options - Optional settings for the request (signal, region, cacheControl)
 * @returns Promise resolving to the object bytes wrapped in a Blob
 * @throws Error if there is an issue downloading the object or request is aborted
 */
export const getBlobFromS3URI = async (s3URI: string, options: GetFileFromS3Options = {}): Promise<Blob> => {
  const { signal } = options;
  const { bucket, s3Key, body, durationMs, contentLength, contentType } = await downloadS3Object(s3URI, options);

  try {
    const bytes = await body.transformToByteArray();
    if (!bytes) {
      throw new Error(`${S3ErrorMessages.DOWNLOAD_FAILED}: bucket: ${bucket}, key: ${s3Key}`);
    }

    logger.debug(
      `Blob downloaded from S3. Bucket: ${bucket}, S3 Key: ${s3Key}, ` +
        `File Size in S3: ${formatBytes(contentLength)}, ` +
        `File Size After Fetch: ${formatBytes(bytes.length)}, Duration: ${durationMs}ms.`
    );

    return new Blob([bytes], { type: contentType ?? 'application/octet-stream' });
  } catch (error: unknown) {
    if (signal?.aborted || isAbortError(error)) {
      throw createAbortError();
    }
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`Error while downloading blob from S3: ${errorMessage}`);
    throw error;
  }
};

// ============================================================================
// S3 Prefix Discovery & Key Listing Utilities
// ============================================================================

/**
 * Lists immediate "folder" prefixes under a given S3 prefix using delimiter-based listing.
 *
 * This is much more efficient than listing all keys recursively when you only need
 * to discover the top-level partitions (e.g., Hive-style partition folders).
 *
 * Uses S3 ListObjectsV2 with Delimiter='/' to get CommonPrefixes, which represent
 * the immediate subdirectories without recursing into them.
 *
 * @param bucket - S3 bucket name
 * @param prefix - S3 key prefix (should end with '/' for folder listing)
 * @param signal - Optional AbortSignal for cancellation
 * @returns Array of immediate prefix strings (e.g., ['prefix/partition=value1/', 'prefix/partition=value2/'])
 */
export const listS3Prefixes = async (bucket: string, prefix: string, signal?: AbortSignal): Promise<string[]> => {
  if (signal?.aborted) {
    throw createAbortError();
  }

  const s3Client = await getS3Client();
  const allPrefixes: string[] = [];
  let continuationToken: string | undefined;

  // Ensure prefix ends with '/' for proper folder listing
  const normalizedPrefix = prefix.endsWith('/') ? prefix : `${prefix}/`;

  try {
    do {
      if (signal?.aborted) {
        throw createAbortError();
      }

      const listCommandInput: ListObjectsV2CommandInput = {
        Bucket: bucket,
        Prefix: normalizedPrefix,
        Delimiter: '/',
        ContinuationToken: continuationToken,
      };

      const listCommand = new ListObjectsV2Command(listCommandInput);
      let response: ListObjectsV2CommandOutput;

      try {
        response = await s3Client.send(listCommand, { abortSignal: signal });
      } catch (error) {
        if (signal?.aborted || isAbortError(error)) {
          throw createAbortError();
        }
        if (error instanceof S3ServiceException) {
          logger.error(`S3 service error in listS3Prefixes: ${error.message}`);
          throw new Error(
            `${S3ErrorMessages.LIST_OBJECTS_FAILED}: bucket: ${bucket}, prefix: ${normalizedPrefix}, error: ${error.message}`
          );
        }
        throw error;
      }

      if (response.CommonPrefixes) {
        const prefixes = response.CommonPrefixes.map((cp) => cp.Prefix).filter((p): p is string => p !== undefined);
        allPrefixes.push(...prefixes);
      }

      continuationToken = response.NextContinuationToken;
    } while (continuationToken);

    logger.info(
      `listS3Prefixes: Found ${allPrefixes.length} prefixes under bucket=${bucket}, prefix=${normalizedPrefix}`
    );
    return allPrefixes;
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    logger.error(`Error in listS3Prefixes: ${error}`);
    throw error;
  }
};

/**
 * Extracts partition values from S3 prefix paths.
 *
 * Given a list of S3 prefixes like:
 *   ['reports/data/verity_product_line_id=129/', 'reports/data/verity_product_line_id=130/']
 * and a partition key 'verity_product_line_id', returns ['129', '130'].
 *
 * @param prefixes - Array of S3 prefix strings
 * @param partitionKey - The Hive-style partition key name to extract values for
 * @returns Sorted array of unique partition values
 */
export const extractPartitionValuesFromPrefixes = (prefixes: string[], partitionKey: string): string[] => {
  const values = new Set<string>();
  const pattern = new RegExp(`${partitionKey}=([^/]+)`);

  for (const prefix of prefixes) {
    const match = prefix.match(pattern);
    if (match?.[1]) {
      values.add(match[1]);
    }
  }

  return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
};

// ============================================================================
// S3 Key Listing & Filtering
// ============================================================================

/**
 * Options for filtering S3 keys after listing
 */
export interface S3KeyFilterOptions {
  /** File suffix to filter by (e.g., '.json', 'data.json') */
  suffix?: string;
  /** Patterns to exclude from results */
  excludePatterns?: string[];
  /** Patterns that must be present in the key */
  includePatterns?: string[];
}

/**
 * Lists all S3 keys under a given prefix with optional filtering.
 *
 * Handles pagination internally via getAllObjectKeysFromS3.
 *
 * @param bucket - S3 bucket name
 * @param prefix - S3 key prefix to list under
 * @param options - Optional filtering criteria
 * @param signal - Optional AbortSignal for cancellation
 * @returns Filtered array of S3 keys
 */
export const listS3Keys = async (
  bucket: string,
  prefix: string,
  options?: S3KeyFilterOptions,
  signal?: AbortSignal
): Promise<string[]> => {
  try {
    logger.info(`listS3Keys: Listing keys in bucket=${bucket}, prefix=${prefix}`);

    const allKeys = await getAllObjectKeysFromS3(bucket, prefix, signal);

    if (!options) {
      logger.info(`listS3Keys: Found ${allKeys.length} keys (no filter applied)`);
      return allKeys;
    }

    const filtered = filterS3Keys(allKeys, options);
    logger.info(`listS3Keys: Found ${allKeys.length} total keys, ${filtered.length} after filtering`);
    return filtered;
  } catch (error) {
    logger.error(`listS3Keys: Error listing keys`, { bucket, prefix, error });
    throw error;
  }
};

/**
 * Filters an array of S3 keys based on suffix, exclude, and include patterns.
 *
 * @param allKeys - Array of S3 keys to filter
 * @param options - Filter options
 * @returns Filtered array of S3 keys
 */
export const filterS3Keys = (allKeys: string[], options: S3KeyFilterOptions): string[] => {
  return allKeys.filter((key) => {
    // Check exclude patterns
    if (options.excludePatterns?.length) {
      if (options.excludePatterns.some((pattern) => key.includes(pattern))) {
        return false;
      }
    }

    // Check include patterns
    if (options.includePatterns?.length) {
      if (!options.includePatterns.some((pattern) => key.includes(pattern))) {
        return false;
      }
    }

    // Check suffix
    if (options.suffix && !key.endsWith(options.suffix)) {
      return false;
    }

    return true;
  });
};

/**
 * Downloads a file from S3 using its URI with automatic AbortController management
 * Creates an AbortController internally and returns both the promise and abort function
 * @param s3URI - The S3 URI of the file to download
 * @param timeoutMs - Optional timeout in milliseconds (default: no timeout)
 * @param region - AWS region for the S3 client (default: DEFAULT_AWS_REGION)
 * @returns Object containing the data promise and abort function
 */
export const getFileFromS3URIWithAbort = <T = unknown>(
  s3URI: string,
  timeoutMs?: number,
  region: AwsRegion = DEFAULT_AWS_REGION
): {
  promise: Promise<T>;
  abort: () => void;
} => {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  // Set up timeout if specified
  if (timeoutMs && timeoutMs > 0) {
    timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);
  }

  const promise = getFileFromS3URI<T>(s3URI, { signal: controller.signal, region }).finally(() => {
    // Clean up timeout when promise resolves or rejects
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  });

  return {
    promise,
    abort: () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      controller.abort();
    },
  };
};
